import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import config from '../auth_config.json';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { AcceptTermsModel, CreateEmployerModel, CreateWorkerModel, Employer,Network,PlanPriceResponse } from './models/SignupModels';
import { CancelReport, Customer, Invoice, Package } from './models/ChargeOverModels';
import { EmployerContactSubscriptionDTO, ChangePlanRequest, Contact, AddHousingPartnerContactLog, AddTransportationPartnerContactLog, Resource, ResourceTopic, ContactWithPermissionsDto, CancelSubscription } from './models/AccountManagementModels';
import { Applicant, BookmarkPositionStatus, GetApplicantsResponse, JobPosition, PositionsInitialDataDTO, PublicEmployer } from './models/JobModels';
import { AuthService } from '@auth0/auth0-angular';
import { Token } from '@angular/compiler';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient,     public auth: AuthService,
  ) {}

  getCompanyDetails(employerId: string, confirmationCode: string): Observable<CreateEmployerModel> {
    return this.http.get(`${config.apiUri}/signup/SignupEmployer/GetCompanyDetails/${employerId}/${confirmationCode}`) as Observable<CreateEmployerModel>;
  }

  getEmployers(): Observable<Employer[]>  {
    return this.http.get(`${config.apiUri}/signup/SignupEmployer/GetEmployers`) as Observable<Employer[]>;
  }

  getPlanPrice(plan: string, yearly: boolean, couponCode: string) {
    return this.http.get(`${config.apiUri}/signup/SignupEmployer/GetPlanPrice/${plan}/${yearly}?couponCode=${couponCode}`) as Observable<PlanPriceResponse>;
  }

  checkConfirmationCode(code: string, employerId: string) {
    return this.http.get(`${config.apiUri}/signup/SignupEmployer/VerifyConfirmationCode/${employerId}/${code}`);    
  }
  
  sendNewConfirmationCode(employerId: number) {
    return this.http.post(`${config.apiUri}/signup/SignupEmployer/ConfirmationCode?employerId=${employerId}`,null);   
  }
  
  sendConfirmationCodeEmail(email: string) {
    return this.http.post(`${config.apiUri}/signup/SignupWorker/ConfirmationCode/`,{email});   
  }
  
  getNetwork(networkId: number): Observable<Network>  {
    return this.http.get(`${config.apiUri}/signup/SignupEmployer/GetNetwork/${networkId}/`) as Observable<Network>;
  }

  createEmployer(createEmployerModel: CreateEmployerModel): Observable<Employer>  {
    return this.http.post(`${config.apiUri}/signup/SignupEmployer/`, createEmployerModel) as Observable<Employer>;
  }

  createWorker(createWorkerModel: CreateWorkerModel): any  {
    return this.http.post(`${config.apiUri}/signup/SignupWorker/`, createWorkerModel) as any;
  }
  
  GetConfigurationParamForSignup(appSetting: string) {
    return this.http.get(`${config.apiUri}/signup/SignupWorker/GetConfigurationParam/${appSetting}`) as Observable<string>;
  }

  GetPageByName(pageName: string): Observable<any> {
    const encodedPageName = encodeURIComponent(pageName);
    return this.http.get(`${config.apiUri}/signup/SignupEmployer/GetPage?name=${encodedPageName}`) as Observable<any>;
  }

  // housing partners
  addHousingPartnerContactLog(addHousingPartnerContactLog: AddHousingPartnerContactLog): any  {
    return this.http.post(`${config.apiUri}/api/PartnerContact/AddHousingPartnerContactLog/`, addHousingPartnerContactLog) as any;
  }

  addTransportationPartnerContactLog(addTransportationPartnerContactLog: AddTransportationPartnerContactLog): any  {
    return this.http.post(`${config.apiUri}/api/PartnerContact/AddTransportationPartnerContactLog/`, addTransportationPartnerContactLog) as any;
  }

  getHousingPartner(housingPartnerId: number): any  {
    return this.http.get(`${config.apiUri}/api/PartnerContact/GetHousingPartner/${housingPartnerId}`) as any;
  }
  
  getActivePartners(): any  {
    return this.http.get(`${config.apiUri}/api/PartnerContact/GetActivePartners/`) as any;
  }

  //Account management
  getPrimaryEmployerForContact(contactId: number) {
    var response = this.http.get(`${config.apiUri}/api/AccountManagement/GetPrimaryEmployerForContact/${contactId}`) as Observable<EmployerContactSubscriptionDTO>;
    return response;
  }
  getEmployerForAdmin(contactId?: number) {
    var response = this.http.get(`${config.apiUri}/api/AccountManagement/GetEmployerForAdmin/${contactId}`) as Observable<EmployerContactSubscriptionDTO>;
    return response;
  }

  getChargeoverProfile(employerId: number) {
    return this.http.get(`${config.apiUri}/api/AccountManagement/GetChargeoverProfile/${employerId}`) as Observable<Customer>;
  }

  getChargeoverSubscription(employerId: number) {
    return this.http.get(`${config.apiUri}/api/AccountManagement/GetSubscriptionForEmployer/${employerId}`) as Observable<Package>;
  }

  getAllChargeoverInvoices(employerId: number) {
    return this.http.get(`${config.apiUri}/api/AccountManagement/GetAllInvoicesForEmployer/${employerId}`) as Observable<Invoice[]>;
  }

  GetConfigurationParam(appSetting: string) {
    return this.http.get(`${config.apiUri}/api/AccountManagement/GetConfigurationParam/${appSetting}`) as Observable<string>;
  }

  CancelChargeoverSubscription(employerId: number, contactId: number, cancelModel: CancelSubscription) {
    return this.http.post(`${config.apiUri}/api/AccountManagement/CancelSubscriptionForEmployer/${employerId}/${contactId}`,cancelModel) as Observable<any>;
  }

  UpdateChargeoverSubscription(employerId: number, contactId: number, changePlanRequest: ChangePlanRequest) {
    return this.http.post(`${config.apiUri}/api/AccountManagement/UpgradeSubscriptionForEmployer/${employerId}/${contactId}`,changePlanRequest) as Observable<any>;
  }

  BuyChargeoverSubscription(employerId: number, contactId: number, changePlanRequest: ChangePlanRequest) {
    return this.http.post(`${config.apiUri}/api/AccountManagement/BuySubscriptionForEmployer/${employerId}/${contactId}`,changePlanRequest) as Observable<any>;
  }

  getContacts(employerId: number) {
    return this.http.get(`${config.apiUri}/api/AccountManagement/AdminOrder/${employerId}`) as Observable<ContactWithPermissionsDto[]>;
  }

  updateContacts(contacts: any) {
    return this.http.post(`${config.apiUri}/api/AccountManagement/UpdateAdminOrder/`,contacts);
  }

  IsFeatureLimited(employerId: number,identifier: string): Observable<number> {
    return this.http.get(`${config.apiUri}/api/AccountManagement/IsFeatureLimited/${employerId}/${identifier}/`) as Observable<number>;
  }

  GetStateName(stateProvId: number) {
    return this.http.get(`${config.apiUri}/api/AccountManagement/GetStateName/${stateProvId}`) as Observable<any>;
  }
  
  acceptTerms(acceptTerms: AcceptTermsModel): Observable<Employer>  {
    return this.http.post(`${config.apiUri}/api/AccountManagement/AcceptTerms/`, acceptTerms) as Observable<Employer>;
  }
  
  GetCancelReport(reportId: number, employerId: number): Observable<CancelReport> {
    return this.http.get(`${config.apiUri}/api/AccountManagement/GetCancelReport/${reportId}/${employerId}/`) as Observable<CancelReport>;
  }
  GetDowngradePendingReport(employerId: number): Observable<CancelReport> {
    return this.http.get(`${config.apiUri}/api/AccountManagement/GetDowngradePendingReport/${employerId}/`) as Observable<CancelReport>;
  }

  getMemberNetworks(): Observable<Network[]>  {
    return this.http.get(`${config.apiUri}/api/AccountManagement/networks/`) as Observable<Network[]>;
  }

  // resources
  getResourcesByTopicId(topicId: number): Observable<Resource[]> {
    return this.http.get<Resource[]>(`${config.apiUri}/api/Resources/${topicId}`);
  }

  getNetworkResourceTopics(): Observable<ResourceTopic[]> {
    return this.http.get<ResourceTopic[]>(`${config.apiUri}/api/Resources/network-topics`);
  }

  // positions
  getPositionsInitialDataForEmployer(employerId: number): Observable<PositionsInitialDataDTO> {
    return this.http.get<PositionsInitialDataDTO>(`${config.apiUri}/api/Positions/GetPositionsInitialDataForEmployer/${employerId}`);
  }


  getPositionsForEmployer(employerId: number): Observable<JobPosition[]> { 
    return this.http.get<JobPosition[]>(`${config.apiUri}/api/Positions/GetPositionsForEmployer/${employerId}`);
  }


  private getHeaders(): Observable<HttpHeaders> {
    return this.auth.getAccessTokenSilently().pipe(
      map(token => { return new HttpHeaders().set('Authorization', `Bearer ${token}`)} ),
      catchError(() => of(new HttpHeaders())) // Return empty headers if token fetch fails
    );
  }

  getPositionsById(positionId: number): Observable<JobPosition> {
    return this.getHeaders().pipe(
      switchMap(headers => this.http.get<JobPosition>(`${config.apiUri}/publicapi/PublicPositions/GetPositionById/${positionId}`, { headers })
      )
    );
  }

  getEmployerForPositionById(employerId: number): Observable<PublicEmployer> {
    return this.getHeaders().pipe(
      switchMap(headers => 
        this.http.get<PublicEmployer>(`${config.apiUri}/publicapi/PublicPositions/GetEmployerForPositionById/${employerId}`, { headers })
      )
    );
  }
  bookmarkPosition(positionId: number, isActive: boolean = true): Observable<any> { 
    return this.http.put(`${config.apiUri}/api/Positions/BookmarkPosition/${positionId}?isActive=`+ isActive, null);
  }

  hidePosition(positionId: number, isActive: boolean = true): Observable<any> { 
    return this.http.put(`${config.apiUri}/api/Positions/hidePosition/${positionId}?isActive=`+ isActive, null);
  }
  
  bookmarkPositionStatus(positionId: number): Observable<BookmarkPositionStatus> { 
    return this.http.get(`${config.apiUri}/api/Positions/BookmarkPositionStatus/${positionId}`) as Observable<BookmarkPositionStatus>;
  }

  savePositionForEmployer(employerId:number, jobPosition: JobPosition): any {
    return this.http.post(`${config.apiUri}/api/Positions/SavePosition/${employerId}`, jobPosition) as Observable<JobPosition>;
  }

  deletePosition(employerPositionId: number): any{
    return this.http.delete(`${config.apiUri}/api/Positions/DeletePosition/${employerPositionId}`, ) as Observable<JobPosition>;
  }

  uploadSingleFileForHousing( containerName: string, employerId:number,formData:FormData): Observable<any> {
    return this.http.post(`${config.apiUri}/api/upload/upload?containerName=${containerName}&employerId=${employerId}`,formData,{
      reportProgress: true,
      observe: 'events',
      responseType: 'text' 
    }) as Observable<any>;
  }

  // applicant review 
  getApplicants(employerId:number, filterParams: any): Observable<GetApplicantsResponse> {
    let params = new HttpParams();
    Object.keys(filterParams).forEach(key => {
      const value = filterParams[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<GetApplicantsResponse>(`${config.apiUri}/api/ApplicantReview/GetApplicants/${employerId}`, { params });
  }

  getSeasons(): Observable<string[]> {
        return this.http.get<string[]>(`${config.apiUri}/api/ApplicantReview/GetSeasons/`);
  }

  archiveApplicants(applicationIds: number[], archive: boolean = true): Observable<any> {
    // Adjust the URL as needed to match your .NET API route.
    return this.http.post(`${config.apiUri}/api/ApplicantReview/ArchiveApplicants?archive=${archive}`, applicationIds);
  }

  updateApplicantStatus(applicationId: number, newStatus: string, sendEmail: boolean, reason?: string): Observable<any> {
    const payload = { applicationId, newStatus, sendEmail, reason };

    return this.http.post(`${config.apiUri}/api/ApplicantReview/updatestatus`, payload);
  }
}
